import { getData } from '../../utils/ajax-utils';
import { removeFieldErrors, setFieldError } from '../../utils/utils';
import { BtnLoadingResetEvent, BtnLoadingWaitEvent } from '../btn-loading';
import { EventDispatcher } from '../event-dispatcher';

type EventMap = {
    entityFounded: Entity;
    reset: undefined;
}

export type EntityIdentifierOptionsType = {
    allowExistingThirdparties: boolean, // Autorise ou non les société déjà enregistrées sur le site
}

type IdentifierType = 'siret' | 'ridet' | 'tva' | 'tahiti';

export type Entity = {
    refExt?: string,
    name?: string,
    address?: string,
    city?: string,
    zipCode?: string,
    countryCode?: string,
    phoneCode?: string|number,
    phoneNumber?: string,
    naf?: string,
    closed?: boolean,
}

type ErrorType = {
    identifierInputEmpty: string|null; // Message d'erreur si le champ contenant l'identifiant d'entité est vide
}

export class EntityIdentifier extends EventDispatcher<EventMap> {
    private readonly options: EntityIdentifierOptionsType;

    private readonly identifierTypes: IdentifierType[] = ['siret', 'ridet', 'tva', 'tahiti'];

    private readonly $identifierTypeRadios: NodeListOf<HTMLInputElement>;
    private readonly $blocIdentifierSearch: HTMLElement;
    private readonly $blocIdentifierError: HTMLElement;
    private readonly $identifierLabel: HTMLLabelElement;
    private readonly $identifierInput: HTMLInputElement;
    private readonly $identifierHelp: HTMLElement;
    private readonly $identifierInputError: HTMLElement;
    private readonly $identifierSearchBtn: HTMLButtonElement;
    private readonly $identifierEditBtn: HTMLButtonElement;

    private selectedIdentifierType: IdentifierType | null;

    private labels: Record<IdentifierType, string> = { siret: '', ridet: '', tva: '', tahiti: '' };
    private placeholders: Record<IdentifierType, string> = { siret: '', ridet: '', tva: '', tahiti: '' };
    private helps: Record<IdentifierType, string> = { siret: '', ridet: '', tva: '', tahiti: '' };
    private readonly errors: ErrorType = { identifierInputEmpty: null };

    constructor(
        options: EntityIdentifierOptionsType,
        $identifierTypeRadios: NodeListOf<HTMLInputElement>,
        $blocIdentifierSearch: HTMLElement,
        $blocIdentifierError: HTMLElement,
        $identifierLabel: HTMLLabelElement,
        $identifierInput: HTMLInputElement,
        $identifierHelp: HTMLElement,
        $identifierInputError: HTMLElement,
        $identifierSearchBtn: HTMLButtonElement,
        $identifierEditBtn: HTMLButtonElement,
    ) {
        super();

        // Initialisation des paramètres de la classe
        this.options = options;
        this.$identifierTypeRadios = $identifierTypeRadios;
        this.$blocIdentifierSearch = $blocIdentifierSearch;
        this.$blocIdentifierError = $blocIdentifierError;
        this.$identifierLabel = $identifierLabel;
        this.$identifierInput = $identifierInput;
        this.$identifierHelp = $identifierHelp;
        this.$identifierInputError = $identifierInputError;
        this.$identifierSearchBtn = $identifierSearchBtn;
        this.$identifierEditBtn = $identifierEditBtn;

        this.selectedIdentifierType = (Array.from($identifierTypeRadios).find($radio => $radio.checked)?.value as IdentifierType) ?? null;

        this.identifierTypes.forEach(identifierType => {
            this.labels[identifierType] = this.$identifierLabel.getAttribute(`data-label-${identifierType}`) ?? '';
            this.placeholders[identifierType] = this.$identifierInput.getAttribute(`data-placeholder-${identifierType}`) ?? '';
            this.helps[identifierType] = this.$identifierHelp.getAttribute(`data-help-${identifierType}`) ?? '';

            this.$identifierLabel.removeAttribute(`data-label-${identifierType}`);
            this.$identifierInput.removeAttribute(`data-placeholder-${identifierType}`);
            this.$identifierHelp.removeAttribute(`data-help-${identifierType}`);
        });

        this.errors.identifierInputEmpty = this.$identifierInput.getAttribute('data-error-empty');

        this.initEvents();
    }

    /**
     * Initialisation des événements
     * 
     * @return {void}
     */
    private initEvents(): void {
        // Event lorsqu'on change de type d'identification d'entité
        this.$identifierTypeRadios.forEach($radio => {
            $radio.addEventListener('change', () => {
                this.onIdentifierTypeRadioChange($radio);
            });

            if ($radio.checked) {
                this.onIdentifierTypeRadioChange($radio, true);
            }
        });

        // Clique sur le bouton de recherche si l'utilisateur appuie sur la touche "Enter"
        // en étant focus sur l'input contenant l'identifiant de son entité
        this.$identifierInput.addEventListener('keydown', evt => {
            if ('Enter' === evt.key) {
                evt.preventDefault();
                this.$identifierSearchBtn.click();
            }
        });

        // Event lorsqu'on clique sur le bouton de recherche de l'entité à l'aide de son numéro d'identification
        this.$identifierSearchBtn.addEventListener('click', evt => {
            evt.preventDefault();

            // Si aucun type d'identification n'est sélectionné, on ne fait rien
            if (!this.selectedIdentifierType) {
                return;
            }

            this.addLoading();
            this.lockFields();

            removeFieldErrors(this.$blocIdentifierSearch);

            this.identifyEntity(this.selectedIdentifierType, this.$identifierInput.value).then(entity => {
                this.showEditBtn();
                this.dispatch('entityFounded', entity);
            }).finally(() => {
                this.removeLoading();
            }).catch(error => {
                this.unlockFields();
                this.hideEditBtn();
                setFieldError(this.$identifierInputError, error);
                this.dispatch('reset', undefined);
            });
        });

        // Event lorsqu'on clique sur le bouton d'édition du numéro d'identification
        this.$identifierEditBtn.addEventListener('click', evt => {
            evt.preventDefault();

            this.unlockFields();
            this.hideEditBtn();
            this.dispatch('reset', undefined);
            this.$identifierInput.focus();
        });

        // Si l'identifiant de l'entité n'a pas été validé, vider le champs avant de soumettre le formulaire
        const $form = this.$blocIdentifierSearch.closest<HTMLFormElement>('form');
        $form?.addEventListener('submit', () => {
            if (!this.isFieldsLocked()) {
                this.$identifierInput.value = '';
            }
        });
    }

    /**
     * Event lorsqu'on change de type d'identification d'entité
     * 
     * @param {HTMLInputElement} $radio
     * @param {boolean} skipReset
     * 
     * @return {void}
     */
    private onIdentifierTypeRadioChange($radio: HTMLInputElement, skipReset: boolean = false): void {
        const isValid = this.isValidIdentifierType($radio.value);

        if (!skipReset) {
            this.resetBlocIdentifierSearch();
            this.dispatch('reset', undefined);

            this.unlockFields();
            this.hideEditBtn();

            removeFieldErrors(this.$blocIdentifierSearch);
        }

        if (isValid) {
            this.selectedIdentifierType = $radio.value as IdentifierType;
            this.changeIdentifierType($radio.value as IdentifierType);
            this.showBlocIdentifierSearch();
        } else {
            this.selectedIdentifierType = null;
            this.showBlocIdentifierError();
        }
    }

    /**
     * Affiche le chargement sur le bouton de validation du numéro d'identification
     * 
     * @return {void}
     */
    private addLoading(): void {
        this.$identifierSearchBtn.dispatchEvent(BtnLoadingWaitEvent);
    }

    /**
     * Masque le chargement sur le bouton de validation du numéro d'identification
     * 
     * @return {void}
     */
    private removeLoading(): void {
        this.$identifierSearchBtn.dispatchEvent(BtnLoadingResetEvent);
    }

    /**
     * Désactive l'édition du numéro d'identification
     * 
     * @return {void}
     */
    private lockFields(): void {
        this.$identifierInput.setAttribute('readonly', 'true');
        this.$identifierSearchBtn.setAttribute('disabled', 'true');
    }

    /**
     * Active l'édition du numéro d'identification
     * 
     * @return {void}
     */
    private unlockFields(): void {
        this.$identifierInput.removeAttribute('readonly');
        this.$identifierSearchBtn.removeAttribute('disabled');
    }

    /**
     * Retourne `true` si l'édition du numéro d'identification est activé
     * 
     * @return {boolean}
     */
    private isFieldsLocked(): boolean {
        return !!this.$identifierInput.getAttribute('readonly');
    }

    /**
     * Affiche le bouton d'édition du numéro d'identification de l'entité
     * 
     * @return {void}
     */
    private showEditBtn(): void {
        this.$identifierEditBtn.classList.remove('hidden');
    }

    /**
     * Masque le bouton d'édition du numéro d'identification de l'entité
     * 
     * @return {void}
     */
    private hideEditBtn(): void {
        this.$identifierEditBtn.classList.add('hidden');
    }

    /**
     * Identifie une entité via son numéro d'identification
     * 
     * @param {IdentifierType} identifierType
     * @param {string} identifier
     * 
     * @throws {string} Message d'erreur si l'entité n'est pas trouvée
     * 
     * @return {Promise<Entity>} Les informations de l'entité si elle est trouvée
     */
    private async identifyEntity(identifierType: IdentifierType, identifier: string): Promise<Entity> {
        // Si le numéro d'identification n'est pas renseigné et qu'un message d'erreur est définit, on retourne ce dernier
        if (this.$identifierInput.value.length === 0 && this.errors.identifierInputEmpty) {
            return Promise.reject(this.errors.identifierInputEmpty);
        }

        return getData(scriptVar.ajaxUrl, {
            action: 'identify_entity',
            allowExistingThirdparties: this.options.allowExistingThirdparties ? '1' : '0',
            identifierType,
            identifier,
        }).then(response => {
            if (response.success) {
                return response.data;
            }

            return Promise.reject(response.data);
        }).catch(error => {
            return Promise.reject(error);
        });
    }

    /**
     * Vérifie si le paramètre d'entrée est un identifierType valide
     * 
     * @param {string} identifierType
     * 
     * @return {identifierType is IdentifierType}
     */
    private isValidIdentifierType(identifierType: string): identifierType is IdentifierType {
        return this.identifierTypes.includes(identifierType as IdentifierType);
    }

    /**
     * Change les textes des éléments pour un identifierType spécifique
     * 
     * @param {IdentifierType} identifierType
     * 
     * @return {void}
     */
    private changeIdentifierType(identifierType: IdentifierType): void {
        this.$identifierLabel.innerHTML = this.labels[identifierType];
        this.$identifierInput.setAttribute('placeholder', this.placeholders[identifierType]);
        this.$identifierHelp.innerHTML = this.helps[identifierType];
    }

    /**
     * Affiche le bloc permettant de rechercher une ThirdParty via son identifiant
     * 
     * @return {void}
     */
    private showBlocIdentifierSearch(): void {
        this.$blocIdentifierSearch.classList.remove('hidden');
        this.$blocIdentifierError.classList.add('hidden');
    }

    /**
     * Masque le bloc permettant de rechercher une ThirdParty via son identifiant et affiche celui d'erreur
     * 
     * @return {void}
     */
    private showBlocIdentifierError(): void {
        this.$blocIdentifierSearch.classList.add('hidden');
        this.$blocIdentifierError.classList.remove('hidden');
    }

    /**
     * Vide l'$identifierInput et supprime les erreurs associées s'il y en a
     * 
     * @return {void}
     */
    private resetBlocIdentifierSearch(): void {
        this.$identifierInput.value = '';
        this.$identifierInputError.innerHTML = '';
        this.$identifierInputError.closest('.form-group')?.classList.remove('has-error');
    }
}
